import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";
import { classNames } from "primereact/utils";
import "./styles.css";
import LoadingFeedback from "../../loadingFeedback";

const ShowTable = ({
  loading,
  data = [],
  columns = [],
  pagination = false,
  rows = 10,
  scrollable = false,
  scrollHeight = "flex",
  stripedRows = false,
  showGlobalFilter = false,
  globalFilterFields = [],
  emptyMessage = "No records found",
  compact = false,
  showTooltips = false,
  selectionMode = null,
  onSelectionChange = () => {},
  rowClassName = null,
  frozenWidth = null
}) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);

  const handleSelectionChange = (e) => {
    setSelectedRow(e.value);
    onSelectionChange(e.value);
  };

  const renderColumn = (column) => {
    const headerClasses = classNames({
      "p-text-bold": true,
      "text-sm": compact,
      [column.headerClassName]: column.headerClassName
    });

    const bodyClasses = classNames({
      "text-sm": compact,
      [column.bodyClassName]: column.bodyClassName
    });

    const widthStyle = column.width
      ? { width: column.width, minWidth: column.width, maxWidth: column.width }
      : {};

    const bodyTemplate = (rowData, { rowIndex }) => {
      const cellValue = column.field ? rowData[column.field] : null;
      const tooltipValue = column.tooltipField
        ? rowData[column.tooltipField]
        : cellValue;

      const cellId = `${column.field}-${rowData.id ?? rowIndex}`;

      return (
        <>
          {showTooltips && column.tooltipField && (
            <Tooltip
              target={`#${cellId}`}
              content={
                tooltipValue && typeof tooltipValue === "object"
                  ? Object.entries(tooltipValue)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(", ")
                  : tooltipValue ?? ""
              }
              position="top"
              mouseTrack
              mouseTrackTop={10}
            />
          )}
          <div
            id={cellId}
            className={bodyClasses}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              ...column.style,
              ...widthStyle
            }}
          >
            {column.body ? column.body(rowData) : cellValue}
          </div>
        </>
      );
    };

    return (
      <Column
        key={column.field || column.header}
        field={column.field}
        header={column.header}
        body={bodyTemplate}
        headerClassName={headerClasses}
        frozen={column.frozen}
        alignHeader={column.alignHeader}
        align={column.align}
        sortable={column.sortable}
        style={{ ...column.style, ...widthStyle }}
        headerStyle={{ ...widthStyle }}
        bodyStyle={{ ...widthStyle }}
      />
    );
  };

  return (
    <LoadingFeedback loading={loading}>
    <div
      className={classNames("show-table-container", {
        "compact-mode": compact
      })}
    >
      {showGlobalFilter && (
        <div className="p-inputgroup mb-3">
          <span className="p-inputgroup-addon">
            <i className="pi pi-search" />
          </span>
          <InputText
            placeholder="Search..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="p-inputtext-sm"
          />
        </div>
      )}

      <DataTable
        value={data}
        stripedRows={stripedRows}
        scrollable={scrollable}
        scrollHeight={scrollHeight === "flex" ? undefined : scrollHeight}
        globalFilterFields={globalFilterFields}
        emptyMessage={emptyMessage}
        paginator={pagination}
        rows={rows}
        rowsPerPageOptions={[5, 10, 25, 50]}
        selectionMode={selectionMode}
        selection={selectedRow}
        onSelectionChange={handleSelectionChange}
        rowClassName={rowClassName}
        size={compact ? "small" : "normal"}
        showGridlines
        className="p-datatable-sm custom-table-header"
      >
        {frozenWidth && (
          <Column
            headerClassName={`table-header-styled table-header-blueBlack`}
            frozen
            body={() => null}
          />
        )}
        {columns.map(renderColumn)}
      </DataTable>
    </div>
    </LoadingFeedback>
  );
};

export default ShowTable;
